/**
 * Unified input.
 *
 * Keyboard/mouse and touch both write into one InputState. Nothing downstream —
 * the player controller, the camera — knows or cares which device produced it.
 * That is what makes "works on phone and PC" a property of two small adapters
 * rather than branching through the whole game.
 */

/* The action list is shared with the help screen and the keyboard map — see
   packages/shared/src/controls.ts. Re-exported so nothing downstream has to
   know it comes from there. */
import type { GameAction } from '@emberreach/shared';
export type { GameAction };

export interface InputState {
  /** Movement intent, -1..1 each, camera-relative. */
  moveX: number;
  moveZ: number;
  running: boolean;
  jumpQueued: boolean;
  /** Camera look delta since last frame, in radians. */
  lookYaw: number;
  lookPitch: number;
  /** Edge-triggered actions, cleared once read. */
  actions: Set<GameAction>;
  /**
   * Clicks and taps on the world, in CSS pixels relative to the canvas.
   *
   * Deliberately *not* resolved here. Turning a screen position into "the
   * ground at x,z" or "that wolf" needs the scene and the camera, and this
   * module knows about neither — its whole job is to be the one place that
   * understands devices, so that nothing downstream has to.
   */
  worldClicks: { x: number; y: number }[];
  /** Wheel notches since the last frame, in browser units. Positive pulls out. */
  zoomDelta: number;
}

export function createInputState(): InputState {
  return {
    moveX: 0, moveZ: 0,
    running: false,
    jumpQueued: false,
    lookYaw: 0, lookPitch: 0,
    actions: new Set(),
    worldClicks: [],
    zoomDelta: 0
  };
}

/**
 * Which control scheme to show.
 *
 * Touch capability alone is the wrong test — plenty of laptops report touch
 * points and would then get a joystick nobody wants. Requiring a coarse pointer
 * as well is what distinguishes a phone or tablet from a touchscreen laptop.
 */
export function detectTouchDevice(win: Window = window): boolean {
  const coarse = win.matchMedia?.('(pointer: coarse)').matches ?? false;
  const hasTouch = 'ontouchstart' in win || (win.navigator?.maxTouchPoints ?? 0) > 0;
  return coarse && hasTouch;
}

export interface InputSource {
  attach(): void;
  detach(): void;
}

/** Reads and clears the edge-triggered parts. Call once per frame. */
export function consumeFrame(state: InputState): {
  moveX: number; moveZ: number; running: boolean; jump: boolean;
  lookYaw: number; lookPitch: number; actions: GameAction[];
  worldClicks: { x: number; y: number }[];
  zoomDelta: number;
} {
  const frame = {
    moveX: state.moveX,
    moveZ: state.moveZ,
    running: state.running,
    jump: state.jumpQueued,
    lookYaw: state.lookYaw,
    lookPitch: state.lookPitch,
    actions: [...state.actions],
    worldClicks: [...state.worldClicks],
    zoomDelta: state.zoomDelta
  };
  state.jumpQueued = false;
  state.lookYaw = 0;
  state.lookPitch = 0;
  state.actions.clear();
  state.worldClicks.length = 0;
  state.zoomDelta = 0;
  return frame;
}
